/**
 * Drives the actual upload of processed guide sections via the existing
 * MarkdownService (/statics API). Uploads each referenced image once, rewrites
 * the markdown image links to the returned blob URLs, then posts one markdown
 * entry per enabled section. The INTRODUCTION section is uploaded as the
 * downloadable full-guide attachment (header download button).
 */

import { MarkdownService, MarkdownImage } from '../services/markdownService';
import { GuideSection } from './guideProcessor';
import { basename } from './zip';

export interface UploadProgress {
  phase: 'images' | 'sections' | 'done';
  current: number;
  total: number;
  message: string;
}

export interface SectionResult {
  id: string;
  title: string;
  pageurl: string;
  datatestid: string;
  status: 'uploaded' | 'skipped' | 'failed';
  detail?: string;
}

export interface UploadOutcome {
  results: SectionResult[];
  imagesUploaded: number;
  missingImages: string[];
}

export interface UploadOptions {
  sections: GuideSection[];
  /** Image bytes keyed by lower-cased basename (e.g. "page008_01.png"). */
  images: Map<string, Uint8Array>;
  /** Original full guide markdown — uploaded as the downloadable attachment. */
  fullGuideText: string;
  guideFilename: string;
  onProgress?: (p: UploadProgress) => void;
}

function guessImageMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop() || '';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'webp') return 'image/webp';
  return 'application/octet-stream';
}

export async function uploadGuide(opts: UploadOptions): Promise<UploadOutcome> {
  const { sections, images, fullGuideText, guideFilename, onProgress } = opts;
  const enabled = sections.filter((s) => s.enabled);

  // 1) Collect every distinct image referenced by an enabled section.
  const neededRefs = new Set<string>();
  for (const s of enabled) s.imageRefs.forEach((r) => neededRefs.add(r));

  const urlByRef = new Map<string, string>();
  const missingImages: string[] = [];
  const refList = [...neededRefs];
  let imagesUploaded = 0;

  for (let i = 0; i < refList.length; i++) {
    const ref = refList[i];
    const key = basename(ref).toLowerCase();
    const bytes = images.get(key);
    onProgress?.({ phase: 'images', current: i, total: refList.length, message: `Uploading image ${basename(ref)}` });
    if (!bytes) {
      missingImages.push(ref);
      continue;
    }
    const name = basename(ref);
    // Copy into a fresh ArrayBuffer-backed Uint8Array so Blob/File gets a clean buffer.
    const fresh = new Uint8Array(bytes);
    const file = new File([fresh], name, { type: guessImageMime(name) });
    const url = await MarkdownService.uploadFile(file, 'image');
    urlByRef.set(ref, url);
    imagesUploaded++;
  }

  // 2) Upload each enabled section.
  const results: SectionResult[] = [];
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    onProgress?.({ phase: 'sections', current: i, total: sections.length, message: s.title });

    if (!s.enabled) {
      results.push({ id: s.id, title: s.title, pageurl: s.pageurl, datatestid: s.datatestid, status: 'skipped', detail: 'No data-testid (conceptual section).' });
      continue;
    }

    try {
      if (s.kind === 'attachment') {
        // Downloadable full guide → upload the whole markdown file as an attachment.
        const file = new File([fullGuideText], guideFilename, { type: 'text/markdown' });
        const url = await MarkdownService.uploadFile(file, 'attachment');
        await MarkdownService.uploadMarkdown({
          id: s.id,
          title: s.title,
          datatestid: s.datatestid,
          pageurl: s.pageurl,
          moduleid: s.moduleid,
          type: 'markdown',
          content: `[${guideFilename}](${url})`,
          enabled: true,
          imageList: [],
          videoList: [],
          attachmentList: [{ url, filename: guideFilename, included: true }],
        });
        results.push({ id: s.id, title: s.title, pageurl: s.pageurl, datatestid: s.datatestid, status: 'uploaded', detail: 'Full guide attachment' });
        continue;
      }

      // Rewrite image links to blob URLs and build the imageList.
      let content = s.content;
      const imageList: MarkdownImage[] = [];
      const missingForSection: string[] = [];
      for (const ref of s.imageRefs) {
        const url = urlByRef.get(ref);
        if (!url) {
          missingForSection.push(basename(ref));
          continue;
        }
        content = content.split(`](${ref})`).join(`](${url})`);
        imageList.push({ url, imagename: basename(ref), included: true });
      }

      await MarkdownService.uploadMarkdown({
        id: s.id,
        title: s.title,
        datatestid: s.datatestid,
        pageurl: s.pageurl,
        moduleid: s.moduleid,
        type: 'markdown',
        content,
        enabled: true,
        imageList,
        videoList: [],
        attachmentList: [],
      });

      results.push({
        id: s.id,
        title: s.title,
        pageurl: s.pageurl,
        datatestid: s.datatestid,
        status: 'uploaded',
        detail: missingForSection.length ? `Uploaded — missing images: ${missingForSection.join(', ')}` : `${imageList.length} image(s)`,
      });
    } catch (err) {
      results.push({
        id: s.id,
        title: s.title,
        pageurl: s.pageurl,
        datatestid: s.datatestid,
        status: 'failed',
        detail: err instanceof Error ? err.message : 'Upload failed',
      });
    }
  }

  onProgress?.({ phase: 'done', current: sections.length, total: sections.length, message: 'Done' });
  return { results, imagesUploaded, missingImages };
}
